import { scaleLinear, scaleQuantile, extent } from 'd3';
import { WIDGET_TYPES } from 'widgets';
import { DefaultCategoricalColors } from 'styles/chartColors';
import { primaryColorLightest, primaryColor } from 'styles/colors';
import { useMemo } from 'react';
import { GenericObject } from 'interfaces/GenericObject';

type ScalesProp = {
	[widgetId in keyof WIDGET_TYPES]: {
		// We don't type the scaleId because a user could add a custom scale, or a scale name we don't have at compile time
		// so just leave it as a string. Widgets won't change after run time so that can be typed like it is
		[ScaleId: string]: { (value: string | number): string; default: undefined } | { default: boolean };
	};
};

const categoricalColorCache = {};
let categoricalColorIndex = 0;

/**
 * @param appSpecificColors
 */
export function useDefaultColors(appSpecificColors) {
	return useMemo(() => {
		const defaultColors = setUpColorScale(SlykitDefaultColors, {
			'*': {
				'Single Color': [primaryColor],
				Categorical: DefaultCategoricalColors,
				Sequential: {
					type: 'sequential',
					scale: [primaryColor, primaryColorLightest],
				},
			},
		});

		if (appSpecificColors) {
			appSpecificColors.addToChain(defaultColors);
			return appSpecificColors;
		}
		return defaultColors;

		// should really only ever fire once at init. Until we support user changing styles during app lifecycle
	}, [appSpecificColors]);
}

export function useAppColors(appColorsConfig) {
	return useMemo(() => {
		return setUpColorScale(ColorScaleCommandHandler, appColorsConfig);
	}, [appColorsConfig]);
}
/**
 * @param type
 * @param config
 */
export function setUpColorScale<T extends ColorScaleCommandHandler>(type: { new (arg1): T }, config): T {
	const scales: ScalesProp = Object.entries(config).reduce<ScalesProp>((coll, [widgetId, scalesConfig]) => {
		//TODO fix ts ignore
		//@ts-ignore
		coll[widgetId] = Object.entries(scalesConfig).reduce((c2, [scaleId, scaleConfig]) => {
			if (Array.isArray(scaleConfig)) {
				handleCategorical(c2, scaleId, scaleConfig);
			} else if (scaleConfig === 'default') {
				handleDefault(c2, scaleId);
			} else if (scaleConfig.hasOwnProperty('type')) {
				if (scaleConfig.type === 'sequential') {
					handleSequential(c2, scaleId, scaleConfig);
				} else if (scaleConfig.type === 'quantile') {
					handleQuantile(c2, scaleId, scaleConfig);
				} else if (scaleConfig.type === 'range') {
					handleRange(c2, scaleId, scaleConfig);
				} else if (scaleConfig.type === 'direct') {
					handleDirect(c2, scaleId, scaleConfig);
				} else {
					throw new Error(`Supplied scale type '${scaleConfig.type}' is unknown`);
				}

				c2[scaleId].scaleType = scaleConfig.type;
			} else {
				console.warn(
					`Defined scale's object does not match correct structure. Color Scale '${scaleId}' will be ignored`
				);
				return c2;
			}
			c2[scaleId].scaleId = scaleId;

			return c2;
		}, {});

		return coll;
	}, {} as ScalesProp);

	return new type(scales);
}

/**
 * Command Chain Pattern. Chain link for colors. Can be used on individual apps or app widgets.
 */
export class ColorScaleCommandHandler {
	private next: ColorScaleCommandHandler;
	protected scales: ScalesProp;
	private filters: GenericObject<Array<string>> = {};

	public constructor(scales: ScalesProp) {
		this.scales = scales;
	}

	public execute(widgetToUse, scaleToUse) {
		if(scaleToUse === undefined) {//if not passed in use the first available option
			return this.execute(widgetToUse, this.getAvailableScales(widgetToUse)[0]);
		}
		if (this.scales.hasOwnProperty(widgetToUse) && this.scales[widgetToUse].hasOwnProperty(scaleToUse)) {
			return this.scales[widgetToUse][scaleToUse];
		}
		else if(this.scales.hasOwnProperty("*") && this.scales["*"].hasOwnProperty(scaleToUse)) {
			return this.scales["*"][scaleToUse];
		}
		else if(this.next) {
			return this.next.execute(widgetToUse, scaleToUse);
		}
		//default if all else fails. Hopefully Slykit default colors are in place to include Single Color or we're throwing the error
		else if(this.scales.hasOwnProperty("*") && this.scales["*"].hasOwnProperty("Single Color"))
			return this.scales["*"]["Single Color"];
		throw new Error('Color chain ran out of links');
	}

	getAvailableScales(widgetId) {
		if(this.filters.hasOwnProperty(widgetId)) {
			return this.filters[widgetId];
		}

		const availScales = [];
		//app widget specific
		if (this.scales.hasOwnProperty(widgetId)) {
			availScales.push(...Object.keys(this.scales[widgetId]));
		}
		//wild card
		if (this.scales.hasOwnProperty('*')) {
			availScales.push(...Object.keys(this.scales["*"]));
		}
		if (this.next) {
			availScales.push(...this.next.getAvailableScales(widgetId));
		}
		return availScales;
	}

	setNext(h) {
		this.next = h;
	}

	public addToChain(h) {
		//When moving between dashboard and explorer the appSpecificScales object gets a new pointer, so the useMemo function in
		//useDefaultColors (above) fires again. That fires this addToChain and what happens is the default colors get added everytime
		//the switch occurs. So we check if its a repeat and discard it if it is.
		if (this.next) {//if this object already has a next pass it along the chain to be added at the end
			this.next.addToChain(h);
		} else if(!(h instanceof SlykitDefaultColors && this instanceof SlykitDefaultColors)) {
			this.next = h;
		}
	}

	public filter(widgetId: string, scalesToKeep: Array<string>) {
		if(Array.isArray(scalesToKeep))
			this.filters[widgetId] = scalesToKeep
	}
}

/**
 * Default color chain link for slykit. Shouldn't be used outside slykit (so it's not exported in slykit's index.tsx file)
 */
export class SlykitDefaultColors extends ColorScaleCommandHandler {
	/*
	There is a special case when adding to chain that we need to know if we are at the end of the line. (Other wise the
	default colors get added over and over again.) So as a way to know if we are at the end of the chain, or to know if the default colors
	have already been added and we don't need to add them again, we have this SlykitDefaultColors object.
	For more details see ColorScaleCommandHandler.addToChain()
	 */
}

//region Handle Color Scales
/**
 * @param c2
 * @param scaleId
 * @param scaleConfig
 */
function handleCategorical(c2, scaleId: string, scaleConfig: any) {
	c2[scaleId] = function (v) {
		v = (v.toLowerCase && v.toLowerCase()) || v;
		return categoricalColorCache[v]?.[scaleId];
	};
	c2[scaleId].domain = (data) => {
		if (data === undefined) {
			return {
				all: Object.keys(categoricalColorCache),
				scoped: data,
			};
		}
		data?.forEach((v) => {
			v = (v.toLowerCase && v.toLowerCase()) || v;
			if (!categoricalColorCache.hasOwnProperty(v)) {
				categoricalColorCache[v] = {};
			}
			if (!categoricalColorCache[v][scaleId]) {
				categoricalColorCache[v][scaleId] = scaleConfig[categoricalColorIndex++ % scaleConfig.length];
			}
		});
	};
	c2[scaleId].scaleType = 'Categorical';
}

/**
 * if a widget wanted to include a default scale from the main app could just specify
 * the name of that scale : default
 * e.g. "Categorical" : "default"
 * @param c2
 * @param scaleId
 */
function handleDefault(c2, scaleId) {
	// needs to be an object bc other things are attached (like .scaleId). So it can't just be a string.
	c2[scaleId] = { default: true };
}

function handleSequential(c2, scaleId, scaleConfig) {
	c2[scaleId] = scaleLinear().range(scaleConfig.scale);
	c2[scaleId]._domain = c2[scaleId].domain;
}

/**
 *
 * @param c2
 * @param scaleId
 * @param scaleConfig
 */
function handleQuantile(c2, scaleId, scaleConfig) {
	c2[scaleId] = scaleQuantile().range(scaleConfig.scale);
	c2[scaleId]._domain = c2[scaleId].domain;
	c2[scaleId].domain = (keys) => {
		if (keys === undefined) return c2[scaleId]._domain();
		c2[scaleId]._domain(extent(keys));
	};
}

/**
 * e.g: 0:blue 100:green. User has defined domain and colors (e.g. percentages)
 * @param c2
 * @param scaleId
 * @param scaleConfig
 */
function handleRange(c2, scaleId, scaleConfig) {
	// TODO test this. tested with donut and labels show %, but values passed to color function are raw values, no the %.
	const orderedKeys = Object.keys(scaleConfig.scale).sort((a, b) => parseFloat(a) - parseFloat(b));
	c2[scaleId] = scaleLinear()
		.domain(orderedKeys.map((d) => parseFloat(d)))
		//scaleConfig.scale could have been reordered above, so need to iterate over it to grab correct color
		.range(orderedKeys.map((d) => scaleConfig.scale[d]));
	/*c2[scaleId]._domain = c2[scaleId].domain;
	c2[scaleId].domain = keys => {

	}*/
}

/**
 * @param c2
 * @param scaleId
 * @param scaleConfig
 */
function handleDirect(c2, scaleId, scaleConfig) {
	c2[scaleId] = (v) => {
		if (scaleConfig.scale.hasOwnProperty(v)) {
			return scaleConfig.scale[v];
		}
		return null;
	};
	c2[scaleId].domain = (keys) => {
		//No op, domain is set by the scaleConfig
		if (keys === undefined)
				return Object.keys(scaleConfig.scale);
		return null;
	};
}
//endregion
